import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { queryClient } from "./queryClient";

/**
 * Scope state for the dashboard: the active tenant (mirrored from the URL) and
 * the per-tenant environment selection. Lives at module scope — not React
 * context — so the tRPC client, created at module scope, can read it per
 * request via getState().
 *
 * Persistence (localStorage, key "loot-dashboard-scope"): lastTenantId and
 * envByTenant survive reloads; routeTenantId does not — the URL is its source of
 * truth and the route's beforeLoad rewrites it before any tenant-scoped
 * component renders. The active environment is derived, never stored:
 * envByTenant[routeTenantId] ?? null, where null means the tenant's live environment
 * (the server's default when no x-loot-environment header is sent).
 */
interface ScopeState {
  /** Tenant from the /tenants/$tenantId route. Not persisted. */
  routeTenantId: string | null;
  /** Last tenant the user visited — remembered for UI, never used to redirect. */
  lastTenantId: string | null;
  /** tenantId -> sandbox environment id. No entry = the tenant's live environment. */
  envByTenant: Record<string, string>;
  setRouteTenant: (id: string | null) => void;
  setActiveEnvironment: (id: string | null) => void;
  /**
   * The environment a tenant opens in, whether or not it is the route tenant: the ⌘K
   * palette sets it before navigating to a result, so the page's first queries already
   * carry the header. null = live. The entry persists like the switcher's, so the next
   * visit to that tenant lands in the same sandbox.
   */
  setEnvironmentForTenant: (tenantId: string, id: string | null) => void;
  /** Sign-out: another user on this browser shouldn't inherit selections. */
  clearPersistedScope: () => void;
}

export const useScopeStore = create<ScopeState>()(
  persist(
    (set, get) => ({
      routeTenantId: null,
      lastTenantId: null,
      envByTenant: {},
      setRouteTenant: (id) => {
        // Called from route beforeLoad, which re-runs per navigation.
        if (id === get().routeTenantId) return;
        set(id ? { routeTenantId: id, lastTenantId: id } : { routeTenantId: null });
        // Query keys carry no tenant/env dimension, so scope changes wipe the
        // cache. resetQueries (not clear) because mounted observers are only
        // notified — and active queries refetched — on reset; clear leaves
        // pages rendering the old scope's data. The derived environment flips
        // with the tenant in this same update, so one reset covers both. The
        // auth session is scope-independent — resetting it would flash the
        // full-screen auth gate on every tenant switch.
        void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      },
      setActiveEnvironment: (id) => {
        const { routeTenantId, envByTenant } = get();
        // Outside tenant scope there is no environment to select.
        if (!routeTenantId) return;
        if ((envByTenant[routeTenantId] ?? null) === id) return;
        const next = { ...envByTenant };
        if (id) next[routeTenantId] = id;
        else delete next[routeTenantId];
        set({ envByTenant: next });
        // Same cache wipe as setRouteTenant: every mounted page refetches under
        // the new x-loot-environment header.
        void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      },
      setEnvironmentForTenant: (tenantId, id) => {
        const { routeTenantId, envByTenant, setActiveEnvironment } = get();
        // For the route tenant this is a switch, and setActiveEnvironment resets the cache;
        // setRouteTenant would not, because the id does not change.
        if (tenantId === routeTenantId) {
          setActiveEnvironment(id);
          return;
        }
        if ((envByTenant[tenantId] ?? null) === id) return;
        const next = { ...envByTenant };
        if (id) next[tenantId] = id;
        else delete next[tenantId];
        // No reset here: the tenant route's beforeLoad resets the cache on arrival.
        set({ envByTenant: next });
      },
      clearPersistedScope: () => set({ envByTenant: {}, lastTenantId: null }),
    }),
    {
      name: "loot-dashboard-scope",
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ lastTenantId: s.lastTenantId, envByTenant: s.envByTenant }),
      // v1 stored the same two fields under their org-era names; carry them over
      // so a redeploy does not forget every remembered environment.
      migrate: (persisted, version) => {
        if (version < 2) {
          const v1 = persisted as { lastOrgId?: string | null; envByOrg?: Record<string, string> };
          return { lastTenantId: v1.lastOrgId ?? null, envByTenant: v1.envByOrg ?? {} };
        }
        return persisted as { lastTenantId: string | null; envByTenant: Record<string, string> };
      },
    },
  ),
);

export const selectActiveEnvironmentId = (s: ScopeState) =>
  s.routeTenantId ? (s.envByTenant[s.routeTenantId] ?? null) : null;
